// ----------------------------------------------------------------------
// <copyright file="Izenda.SecurityPolicies.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var constants = require('logic/data/Izenda.SecurityPoliciesConstant');

  const { forEach, isNil, isEmpty, toNumber, isUndefined, isString, keys, isBoolean, isNumber } = require('lodash/fp');

  function SecurityPolicies(data) {
    var props = {
      id: null,
      state: 0,
      inserted: false,
      version: null,
      created: null,
      createdBy: null,
      modified: null,
      modifiedBy: null,
      /* Password Policies */
      // Password Complexity
      minNumberOfPasswordLenght: null,
      maxNumberOfPasswordLenght: null,
      passwordLength: false,
      minNumberOfSpecialCharacter: null,
      maxNumberOfSpecialCharacter: null,
      numberSpecialCharacters: false,
      minNumberOfUppercaseCharacter: null,
      maxNumberOfUppercaseCharacter: null,
      numberUppercaseCharacters: false,
      minNumberOfLowercaseCharacter: null,
      maxNumberOfLowercaseCharacter: null,
      numberLowercaseCharacters: false,
      minNumberOfNumericCharacter: null,
      maxNumberOfNumericCharacter: null,
      numberNumericCharacters: false,
      maxNumberOfRepeatSequential: null,
      maxNumberSequentialRepeated: false,
      // Password Validity
      minNumberOfPasswordAge: null,
      maxNumberOfPasswordAge: null,
      passwordAge: false,
      // Password Expiry Notification
      notifyUseDuring: null,
      notifyUser: false,
      // Password History
      numberOfPasswordToKeep: null,
      enforcePasswordHistory: false,
      // Password Link
      passwordLinkValidity: null,
      passwordLinkValidityChecked: false,
      // Security Questions
      numberOfQuestionProfile: null,
      numberOfQuestionResetPassword: null,
      useSecurityQuestions: false,
      /* Account Lockout Policies */
      numberOfFailedLogonAllowed: null,
      numberFailedLogonAttemptsAllowed: false,
      numberOfFailedAnswerAllowed: null,
      numberFailedSecurityQuestionAttemptsAllowed: false,
      lockoutPeriod: null,

      /* User policies */
      hideUserId: false,
      hideUserProfile: false,

      // calculated value
      minPasswordLength: function() {
        return (
          this.toNumber(this.minNumberOfSpecialCharacter) +
          this.toNumber(this.minNumberOfUppercaseCharacter) +
          this.toNumber(this.minNumberOfLowercaseCharacter) +
          this.toNumber(this.minNumberOfNumericCharacter)
        );
      },
      maxPasswordLength: function() {
        return (
          this.toNumber(this.maxNumberOfSpecialCharacter) +
          this.toNumber(this.maxNumberOfUppercaseCharacter) +
          this.toNumber(this.maxNumberOfLowercaseCharacter) +
          this.toNumber(this.maxNumberOfNumericCharacter)
        );
      },
      enableSecurityQuestionToResetPassword: function() {
        return this.useSecurityQuestions && this.toNumber(this.numberOfQuestionProfile) > 0;
      },
      enableLockoutPeriod: function() {
        return (
          this.toNumber(this.numberOfFailedLogonAllowed) !== 0 || this.toNumber(this.numberOfFailedAnswerAllowed) !== 0
        );
      }
    };

    m.extendObservable(trackable(this), props);
    this.readSecuritPolicies(data);
  }

  /**
   * Read Securtity Data
   */
  SecurityPolicies.prototype.readSecuritPolicies = m.action(function(data) {
    data = data || {};
    forEach(
      function(key) {
        if (!isUndefined(this[key])) {
          this[key] = data[key];
        }
      }.bind(this),
      keys(data)
    );

    // populate data
    this.populateData();

    // Reset Dirty
    this.resetDirty();
  });

  /**
   * update Value by Key
   * @param  {string} key
   * @param  {string} value
   */
  SecurityPolicies.prototype.updateValueByKey = function(key, value, requiredMinValue) {
    requiredMinValue = requiredMinValue || constants.REQUIRED_MIN_VALUE;
    if (!isBoolean(value)) {
      if (isString(value) && isEmpty(value)) {
        this[key] = null;
      } else {
        value = this.toNumber(value);
        if (isNumber(value) && value >= requiredMinValue) {
          this[key] = value;
        }
      }
    } else {
      this[key] = value;
    }
    this.markDirty();
  };

  /**
   * check Has Data
   * @param  {string} min
   * @param  {string} max
   * @return {boolean}
   */
  SecurityPolicies.prototype.checkHasData = function(min, max) {
    return !isNil(min) || !isNil(max);
  };

  /**
   * populate checkbox Data
   */
  SecurityPolicies.prototype.populateData = m.action(function() {
    this.passwordLength = this.checkHasData(this.minNumberOfPasswordLenght, this.maxNumberOfPasswordLenght);
    this.numberSpecialCharacters = this.checkHasData(
      this.minNumberOfSpecialCharacter,
      this.maxNumberOfSpecialCharacter
    );
    this.numberUppercaseCharacters = this.checkHasData(
      this.minNumberOfUppercaseCharacter,
      this.maxNumberOfUppercaseCharacter
    );
    this.numberLowercaseCharacters = this.checkHasData(
      this.minNumberOfLowercaseCharacter,
      this.maxNumberOfLowercaseCharacter
    );
    this.numberNumericCharacters = this.checkHasData(
      this.minNumberOfNumericCharacter,
      this.maxNumberOfNumericCharacter
    );
    this.maxNumberSequentialRepeated = this.checkHasData(this.maxNumberOfRepeatSequential);
    this.passwordAge = this.checkHasData(this.minNumberOfPasswordAge, this.maxNumberOfPasswordAge);
    this.notifyUser = this.checkHasData(this.notifyUseDuring);
    this.enforcePasswordHistory = this.checkHasData(this.numberOfPasswordToKeep);
    this.passwordLinkValidityChecked = this.checkHasData(this.passwordLinkValidity);
    this.useSecurityQuestions = this.checkHasData(this.numberOfQuestionProfile, this.numberOfQuestionResetPassword);
    this.numberFailedLogonAttemptsAllowed = this.checkHasData(this.numberOfFailedLogonAllowed);
    this.numberFailedSecurityQuestionAttemptsAllowed = this.checkHasData(this.numberOfFailedAnswerAllowed);
  });

  /**
   * toNumber
   * @param  {number} number
   * @return {number}
   */
  SecurityPolicies.prototype.toNumber = function(number) {
    if (isString(number) && isEmpty(number)) {
      return null;
    }
    return toNumber(number) || 0;
  };

  /**
   * auto Calculate Number of Min Password Length
   * @return
   */
  SecurityPolicies.prototype.autoCalculateMinPasswordLength = function() {
    if (this.minNumberOfPasswordLenght < this.minPasswordLength) {
      this.minNumberOfPasswordLenght = this.minPasswordLength;
    }
  };

  /**
   * auto Calculate Number of Max Password Length
   * @return {}
   */
  SecurityPolicies.prototype.autoCalculateMaxPasswordLength = function() {
    if (this.maxNumberOfPasswordLenght < this.maxPasswordLength) {
      this.maxNumberOfPasswordLenght = this.maxPasswordLength;
    }
  };

  /**
   * get Data From Setting from UI
   * @return {value}
   */
  SecurityPolicies.prototype.getDataFromSetting = function(condition, value) {
    if (isNil(value)) {
      return value;
    }
    return condition ? this.toNumber(value) : null;
  };

  /**
   * get Data For Save
   * @return {object}
   */
  SecurityPolicies.prototype.getDataForSave = m.action(function() {
    var data = {
      id: this.id,
      state: this.state,
      inserted: this.inserted,
      version: this.version,
      created: this.created,
      createdBy: this.createdBy,
      modified: this.modified,
      modifiedBy: this.modifiedB,
      /* Password Policies */
      // Password Complexity
      minNumberOfPasswordLenght: this.getDataFromSetting(this.passwordLength, this.minNumberOfPasswordLenght),
      maxNumberOfPasswordLenght: this.getDataFromSetting(this.passwordLength, this.maxNumberOfPasswordLenght),
      minNumberOfSpecialCharacter: this.getDataFromSetting(
        this.numberSpecialCharacters,
        this.minNumberOfSpecialCharacter
      ),
      maxNumberOfSpecialCharacter: this.getDataFromSetting(
        this.numberSpecialCharacters,
        this.maxNumberOfSpecialCharacter
      ),
      minNumberOfUppercaseCharacter: this.getDataFromSetting(
        this.numberUppercaseCharacters,
        this.minNumberOfUppercaseCharacter
      ),
      maxNumberOfUppercaseCharacter: this.getDataFromSetting(
        this.numberUppercaseCharacters,
        this.maxNumberOfUppercaseCharacter
      ),
      minNumberOfLowercaseCharacter: this.getDataFromSetting(
        this.numberLowercaseCharacters,
        this.minNumberOfLowercaseCharacter
      ),
      maxNumberOfLowercaseCharacter: this.getDataFromSetting(
        this.numberLowercaseCharacters,
        this.maxNumberOfLowercaseCharacter
      ),
      minNumberOfNumericCharacter: this.getDataFromSetting(
        this.numberNumericCharacters,
        this.minNumberOfNumericCharacter
      ),
      maxNumberOfNumericCharacter: this.getDataFromSetting(
        this.numberNumericCharacters,
        this.maxNumberOfNumericCharacter
      ),
      maxNumberOfRepeatSequential: this.getDataFromSetting(
        this.maxNumberSequentialRepeated,
        this.maxNumberOfRepeatSequential
      ),
      // Password Validity
      minNumberOfPasswordAge: this.getDataFromSetting(this.passwordAge, this.minNumberOfPasswordAge),
      maxNumberOfPasswordAge: this.getDataFromSetting(this.passwordAge, this.maxNumberOfPasswordAge),
      // Password Expiry Notification
      notifyUseDuring: this.getDataFromSetting(this.notifyUser, this.notifyUseDuring),
      // Password History
      numberOfPasswordToKeep: this.getDataFromSetting(this.enforcePasswordHistory, this.numberOfPasswordToKeep),
      // Password Link
      passwordLinkValidity: this.getDataFromSetting(this.passwordLinkValidityChecked, this.passwordLinkValidity),
      // Security Questions
      numberOfQuestionProfile: this.getDataFromSetting(this.useSecurityQuestions, this.numberOfQuestionProfile),
      numberOfQuestionResetPassword: this.getDataFromSetting(
        this.enableSecurityQuestionToResetPassword,
        this.numberOfQuestionResetPassword
      ),
      /* Account Lockout Policies */
      numberOfFailedLogonAllowed: this.getDataFromSetting(
        this.numberFailedLogonAttemptsAllowed,
        this.numberOfFailedLogonAllowed
      ),
      numberOfFailedAnswerAllowed: this.getDataFromSetting(
        this.numberFailedSecurityQuestionAttemptsAllowed,
        this.numberOfFailedAnswerAllowed
      ),
      lockoutPeriod: this.getDataFromSetting(this.enableLockoutPeriod, this.lockoutPeriod),
      hideUserProfile: this.hideUserProfile,
      hideUserId: this.hideUserId
    };

    return data;
  });

  module.exports = SecurityPolicies;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SecurityPolicies.js